
    import Vue from "vue";
    import Component from "vue-class-component";
    import { Prop, Watch } from "vue-property-decorator";
    //import { VMenu } from "@/vMenu";
    import draggable from "vuedraggable";
    import * as toastr from "toastr";

    import Utils from "@/utilities/Utils";
    import { mapData } from "@/utilities/DataMapping";
    import ApiClient from "@/utilities/ApiClient";
    import Colours from "@/utilities/Colours";
    import BriefingMatrixSection from "@/vue/components/BriefingMatrixSection.vue";
    import BriefingMatrixMenu from "@/vue/components/BriefingMatrixMenu.vue";
    import ApiButton from "@/vue/components/ApiButton.vue";
    import PrimaryButton from "@/vue/components/PrimaryButton.vue";
    import PathwayPageSelect from "@/vue/components/PathwayPageSelect.vue";
    import PathwayDialogue from "@/vue/components/PathwayDialogue.vue";
    import PathwayRecipientsDialogue from "@/vue/components/PathwayRecipientsDialogue.vue";

    //import NumericId from "@/vue/components/NumericId.vue";
    import { firstPageID, previewVisitID } from "@/model/Enums";
    import { Site } from "@/model/Site";
    import { Briefing } from "@/model/Briefing";
    import { IPathway, Pathway } from "@/model/Pathway";
    import { BriefingItemContainer, IBriefingItemContainer } from "@/model/BriefingItemContainer";
    import { IPage, Page } from "@/model/Page";
    import { BriefingSection } from "@/model/BriefingSection";
    import { PathwayPage } from "@/model/PathwayPage";
    import { ISaveResponse } from "@/model/ISaveResponse";

    // can be a page or a section


    interface IPageDragChangeEvent {
        added?: { element: BriefingItemContainer; newIndex: number; };
        removed?: { element: BriefingItemContainer; oldIndex: number; };
        moved?: { element: BriefingItemContainer; oldIndex: number; newIndex: number; };
    }

    interface IPathwayDragChangeEvent {
        added?: { element: Pathway; newIndex: number; };
        removed?: { element: Pathway; oldIndex: number; };
        moved?: { element: Pathway; oldIndex: number; newIndex: number; };
    }

    interface IPathwayPageMatrix {
        pathways: Array<IPage>;
        pages: Array<IPathway>;
    }

    @Component({ components: {
        ApiButton,
        PrimaryButton,
        PathwayPageSelect,
        PathwayDialogue,
        PathwayRecipientsDialogue,
        draggable,
        BriefingMatrixSection,
        BriefingMatrixMenu
    } })
    export default class BriefingMatrix extends Vue {

        mounted(): void {

            // Vue.nextTick(() => {
            //     console.log("BriefingMatrix W matrixTable=", (this.$refs?.table as any)?.$el?.clientWidth ?? -1)
            // });

            // I can't find a nice CSS way to position the button so here's brute force:
            const rootDiv = document.getElementsByClassName("root-div")[0];
            const matrixAddButton: HTMLElement = document.getElementsByClassName("matrix-add-button")[0] as HTMLElement;
            const resizeObserver = new ResizeObserver(entries => {
                const rect = rootDiv.getBoundingClientRect();
                const right = rect.x + rect.width;
                matrixAddButton.style.left = String(right - 75) + "px";
            });
            resizeObserver.observe(rootDiv);
        }

        //
        // -- properties
        //

        @Prop({ default: 176 })
        positionTop!: number;

        @Prop({ required: true })
        briefing!: Briefing;

        @Prop({ required: true })
        site!: Site;

        @Prop({ default: "" })
        selectedPageID!: string

        dragHandleImageUrl = require("@/images/drag-vert.svg");

        pathwayList: Array<Pathway> = [];

        // split pages into two sets (works better with dragging than using computed properties with filters)
        briefingPageList: Array<BriefingItemContainer> = [];
        quizPageList: Array<BriefingItemContainer> = [];

        // remove pages in collapsed pages
        get briefingPageListFiltered(): Array<BriefingItemContainer> {
            const collapsedSectionIDs = this.briefingPageList.filter(i => i.isSection && i.section?.isExpanded === false).map(i => i.section?.id ?? "");
            return this.briefingPageList.filter(i => i.isSection || collapsedSectionIDs.indexOf(i.page?.sectionID ?? "---") === -1);
        }
        get quizPageListFiltered(): Array<BriefingItemContainer> {
            const collapsedSectionIDs = this.quizPageList.filter(i => i.isSection && i.section?.isExpanded === false).map(i => i.section?.id ?? "");
            return this.quizPageList.filter(i => i.isSection || collapsedSectionIDs.indexOf(i.page?.sectionID ?? "---") === -1);
        }

        pathwayPageList: Array<PathwayPage> = [];
        selectedPathwayID: string = "";
        shouldShowNoPageDialogue: boolean = false;
        noPageDialogueMessageHtml: string = "";

        pageHeaderCellCssClass(item: BriefingItemContainer): Record<string, boolean> {
            return {
                "page-header-cell": true,
                "active": this.selectedPageID === item.page?.id,
                "no-selection": item.page?.hasSelectedPathway !== true
            }
        }

        pathwayPageCellCssClass(pathwayPage: PathwayPage): Record<string, boolean> {
            return {
                "no-selection": pathwayPage.isUnusedPageOrPathway
            }
        }

        pathwayCssClass(pathway?: Pathway): Record<string, boolean> {
            return {
                active: this.selectedPathwayID === pathway?.id,
                "no-selection": pathway?.hasSelectedPage === false
            };
        }

        get sectionHeaderColourStyle(): Record<string, string> {
            const buttonColour = this.$store.state.clientSettings?.client?.buttonColour ?? "#88F";
            const rgb = Colours.hexToRgb(buttonColour);
            const lum = Colours.luminance(rgb); // 0 - 255 
            // darker = lower alpha = more transparent
            const alpha = lum / 300;


            return { backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})` };
        } 

        //
        // -- watchers, etc.
        //

        dontSaveBriefingOnChange = false; // dumb ass flag for when we don't want this...
        @Watch("briefing", { immediate: true, deep: true })
        async onBriefingChanged(val: Briefing, old: any): Promise<void> {
            console.log("oo briefing changed... dontSaveBriefingOnChange = ", this.dontSaveBriefingOnChange);
            if (!this.dontSaveBriefingOnChange) await this.load();           
        }

        async onPathwaySaved(pathwayID: string): Promise<void> {
            await this.load();
            this.selectedPathwayID = pathwayID;
        }

        //
        // -- load
        //

        async load(): Promise<void> {
            if(Utils.isEmptyId(this.briefing.id)) {
                this.pathwayList = [];
                this.briefingPageList = [];
                this.quizPageList = [];
                return;
            }
            const matrix: IPathwayPageMatrix = await ApiClient.get(`/api/briefing/matrix?briefingID=${this.briefing.id}`);
            this.mapLoadResponse(matrix);
        }

        async mapLoadResponse(matrix: IPathwayPageMatrix): Promise<void> {
            const pathwayMapping = { root: (p: IPathway) => new Pathway(p) };
            this.pathwayList = mapData<Array<Pathway>>(matrix.pathways, pathwayMapping);
            const pageMapping = { root: (p: IPage) => new Page(p) };
            const pageList = mapData<Array<Page>>(matrix.pages, pageMapping);

            const briefingSectionItems = this.briefing.sections.filter(s => !s.isQuiz).map(s => new BriefingItemContainer({ isMenuShowing: false, section: s }));
            const quizSectionItems = this.briefing.sections.filter(s => s.isQuiz).map(s => new BriefingItemContainer({ isMenuShowing: false, section: s }));
 
            this.briefingPageList = pageList.filter(p => !p.isQuiz).map(p => new BriefingItemContainer({isMenuShowing: false, page: p })).concat(briefingSectionItems);
            this.quizPageList = pageList.filter(p => p.isQuiz).map(p => new BriefingItemContainer({isMenuShowing: false, page: p })).concat(quizSectionItems);

            // make sure we can sort the pages within sections
            this.setSectionDisplayOrderForPages(this.briefingPageList);
            this.setSectionDisplayOrderForPages(this.quizPageList);

            // sort sections in with pages
            this.briefingPageList.sort((a, b) => Utils.compareStrings(a.displayOrder, b.displayOrder));
            this.quizPageList.sort((a, b) => Utils.compareStrings(a.displayOrder, b.displayOrder));

// for(const i of this.quizPageList) {
//     console.log(`"...q - ${i.id} | ${i.displayOrder} | sid:${i.page?.sectionID }| sdo:${i.page?.sectionDisplayOrder } | ${i.description} `);
// }

            // flag any empty rows and columns so we can colour them grey
            this.checkEmptyRowsAndColumns();
        }

        setSectionDisplayOrderForPages(items: Array<BriefingItemContainer>) {
            for(const item of items) {
                if(!item.page) continue;
                const section = this.briefing.sections.find(s => s.id === item.page?.sectionID);
                item.page.sectionDisplayOrder = section?.id ? section.displayOrder : 0;
            }
        }

        // this is a bit f**king painful... (face it, this whole component is a f***ing nightmare)
        checkEmptyRowsAndColumns(): void {
            const briefingPages = this.briefingPageList.filter(i => i.isPage).map(i => i.page as Page);
            const quizPages = this.quizPageList.filter(i => i.isPage).map(i => i.page as Page);
            const pathwayHasSelectedMap: Record<string, boolean> = {};
            this.pathwayList.forEach(p => pathwayHasSelectedMap[p.id] = false);
            const pageHasSelectedMap: Record<string, boolean> = {};
            briefingPages.forEach(p => pageHasSelectedMap[p.id] = false);
            quizPages.forEach(p => pageHasSelectedMap[p.id] = false);
            for(const page of briefingPages) {
                for(const pw of page.pathways) {
                    if(pw.isSelected) {
                        pathwayHasSelectedMap[pw.pathwayID] = true;
                        pageHasSelectedMap[pw.pageID] = true;
                    }
                }
            }
            for(const page of quizPages) {
                for(const pw of page.pathways) {
                    if(pw.isSelected) {
                        pathwayHasSelectedMap[pw.pathwayID] = true;
                        pageHasSelectedMap[pw.pageID] = true;
                    }
                }
            }

            // use maps we've built up to set the flags...
            for(const pathway of this.pathwayList) pathway.hasSelectedPage = pathwayHasSelectedMap[pathway.id];
            for(const page of briefingPages.concat(quizPages)) {
                page.hasSelectedPathway = pageHasSelectedMap[page.id];
                for(const pathwayPage of page.pathways) {
                    pathwayPage.isUnusedPageOrPathway = !page.hasSelectedPathway || this.pathwayList.find(p => p.id === pathwayPage.pathwayID)?.hasSelectedPage === false;
                }           
            }
        }

        //
        // -- sections
        //

        toggleSection(section?: BriefingSection) {
            if(!section) return;
            console.log("+++ @@@ section = ", section);
            section.isExpanded = !section.isExpanded;
        }

        //
        // -- new draggery
        //

        onDragStart(event: DragEvent, item: BriefingItemContainer) {
            const data = JSON.stringify(item);
            console.log(`DRAG START - `, data);
            event.dataTransfer?.setData("text/plain", JSON.stringify(item));
            event.dataTransfer?.setData(item.isQuiz ? "quiz" : "briefing", ""); // hack 'cos you CANNOT access getData in onDragOver !!!!
        }

        // we must prevent dropping non-quiz items into quiz and vice-versa
        onDragOver(event: DragEvent, item: BriefingItemContainer) {
            if(!event.dataTransfer?.types) return;
            // using types is an insane HACK.  You cannot access getData becuase of security, apparently.
            const draggedItemIsQuiz = event.dataTransfer.types.indexOf("quiz") > -1;
            if(item.isQuiz === draggedItemIsQuiz) {
                 // prevetDefault means it's cool to drop here
                 event.preventDefault();
             }
        }

        onDrop(event: DragEvent, targetItem: BriefingItemContainer) {
// console.log(`💧 onDrop - target:`, targetItem);
            const draggedItemJson = event.dataTransfer?.getData("text/plain");
            if (!draggedItemJson  || !draggedItemJson.startsWith("{")) return;
            const sourceItem = new BriefingItemContainer(JSON.parse(draggedItemJson));

            if (sourceItem.id === targetItem.id) return; // dragging on self?
            if (targetItem.isQuiz !== sourceItem.isQuiz) return; // different lists (shouldn't happen)

            if(sourceItem.isSection) {
                this.dropSection(sourceItem, targetItem);
            }
            else {
                this.dropPage(sourceItem, targetItem);
            }
        }

        async dropSection(sourceItem: BriefingItemContainer, targetItem: BriefingItemContainer) {
            const sourceSection = sourceItem.section;
            if(!sourceSection) return;

//console.log("💧 dropSection - source: ", sourceItem);
//console.log("💧 dropSection - target: ", targetItem);

            // find section we dropped on (if page, work back up list and find it)
            const targetSection = targetItem.isPage
                ? this.findSection(targetItem.page, false)
                : targetItem?.section;

//console.log("💧 dropSection - targetSection: ", targetSection);

            // find out where draggged section begins and ends
            let sourceSectionAt = this.findStartAndEndOfSection(sourceSection);
//console.log("💧 dropSection - sourceSectionAt: ", sourceSectionAt);
            if(sourceSectionAt.startAt === -1) return;

            const isDraggingEmptySection = sourceSectionAt.endAt - sourceSectionAt.startAt === 0;

            // if the dragged section contains pages - we need to create a NEW section to contain the orphan group of pages that will be displaced
            if(!targetSection && !isDraggingEmptySection) {
//console.log("💧 dropSection - add new section...");
                this.addNewTopSection(sourceSection.isQuiz);
                sourceSectionAt = this.findStartAndEndOfSection(sourceSection); // will have moved so do again
            }

//console.log("💧 dropSection - targetSection: ", targetSection);

            // normally pasted item goes AFTER the target section - but not if we're dropping on the top section
            const isTargetAtTop = (targetSection ? this.findStartAndEndOfSection(targetSection).startAt : 0) === 0;

            // remmove...
            const items = sourceSection.isQuiz ? this.quizPageList : this.briefingPageList;
            const removedSourceItems = items.splice(sourceSectionAt.startAt, sourceSectionAt.endAt - sourceSectionAt.startAt + 1);

//console.log("💧 dropSection - items: ", items);
//console.log("💧 dropSection - removed: ", removedSourceItems);

            // find where to put it... (we are not allowing psting in the MIDDLE of a section!)
            const targetSectionAt = targetSection
                ? this.findStartAndEndOfSection(targetSection)
                : { startAt: 0, endAt: 0 };

// console.log("💧 dropSection - targetSectionAt: ", targetSectionAt);
// console.log("💧 dropSection - isTargetAtTop: ", isTargetAtTop);
// console.log("💧 dropSection - spliacew back at: ", isTargetAtTop ? 0 : targetSectionAt.endAt + 1);

            // put it back
            // (normally we want to paste AFTER the target but if target it at top, paste BEFORE)
            isTargetAtTop
                ? items.splice(0, 0, ...removedSourceItems)
                : items.splice(targetSectionAt.endAt + 1, 0, ...removedSourceItems);

            // save all the things
            this.renumberDisplayOrder(items);
            await this.savePages();
            await this.saveSections();
        }

        async dropPage(sourceItem: BriefingItemContainer, targetItem: BriefingItemContainer) {
            const sourcePage = sourceItem.page;
            if(!sourcePage) return;

            const targetSection = targetItem.isPage
                ? this.findSection(targetItem.page, false)
                : targetItem?.section;
//console.log("💧 dropPage - targetSection: ", targetSection);

            //const sourceSection = this.findSection(sourcePage, false);

            const items = sourcePage.isQuiz ? this.quizPageList : this.briefingPageList;
            const sourceIndex = items.findIndex(i => i.id === sourcePage.id);
            if(sourceIndex === -1) return; // shouldn't happen

            // where's it going?
            const targetIndex = targetItem.isPage
                ? items.findIndex(i => i.id === targetItem.id) // + 1?
                : targetSection
                    ? items.findIndex(i => i.id === targetSection.id) + 1 // add one becaue it goes AFTER the index of the section
                    : 0; // no section - top of list
//console.log("💧 dropPage - targetIndex: ", targetIndex);
            if(targetIndex === -1) {
                console.warn("Failed to determine target index!");
            }

            // remove item
            const removedItem = items.splice(sourceIndex, 1)[0];
            // if different (or no) section, need to update sectionID
            if (removedItem.page) {
                removedItem.page.sectionID = targetSection?.id ?? "";
                removedItem.page.sectionDisplayOrder = targetSection?.displayOrder ?? 0;
            } 
            // slot it back in
            items.splice(targetIndex, 0, removedItem);

            // save (no need to save sections)
            this.renumberDisplayOrder(items);
            await this.savePages();
        }

        //
        // -- pathway drag-drop (using vuedraggable - TODO??? - replace with HTML5 events and do manually?)
        //

        async onPathwayDragChange(changeEvent:  IPathwayDragChangeEvent): Promise<void> {
            if(!changeEvent.moved) return;

            const idList = this.pathwayList.map(p => p.id);
            const matrix: IPathwayPageMatrix = await ApiClient.post(`/api/briefing/reorderPathways?briefingID=${this.briefing.id}`, idList);
            // we need to reload all the pages + pathway-pages!
            this.mapLoadResponse(matrix);
        }

        //
        // -- other UI actions...
        //

        goToPreview(pathway: Pathway): void {
            // const menu = this.$refs.pathwayMenu as VMenu;

            // can we actually go to the selected page for the given pathway?
            if(this.selectedPageID) {
                let page = this.briefingPageList.find(i => i.page?.id === this.selectedPageID)?.page;
                if (!page) page = this.quizPageList.find(i => i.page?.id === this.selectedPageID)?.page;
                if(!page?.pathways.some(p => p.pathwayID === pathway.id && p.isSelected)) {
                    this.shouldShowNoPageDialogue = true;
                    this.noPageDialogueMessageHtml = `The selected <b>${page?.description??"- - -"}</b> page does not exist on the <b>${pathway.description}</b> pathway`;
                    return;
                }
            }
            const pageID = this.selectedPageID ? this.selectedPageID : firstPageID;
            const url = `/briefing/${previewVisitID}/${pageID}?previewPathwayID=${pathway.id}`;
            const win = window.open(url, "_blank");
            win?.focus();
        }

        showRecipients(pathway: Pathway): void {
            this.selectedPathwayID = pathway.id; // for active css - but not used?
            const dialog = this.$refs.pathwayRecipientsDialogue as PathwayRecipientsDialogue;
            dialog.show(pathway.id);
        }

        async onSelectedChanged(pathwayPage: PathwayPage): Promise<void> {
            // TODO - maybe disable matrix cell while saving?
            const response: ISaveResponse = await ApiClient.post("/api/pathwayPage/save", pathwayPage);
            toastr.info(pathwayPage.isSelected ? "Selected" : "De-selected");
            this.checkEmptyRowsAndColumns();
        }

        pathwayEdit(pathway: Pathway): void {
            this.selectedPathwayID = pathway.id; // for active css - but not used?
            const dialog: PathwayDialogue = this.$refs.pathwayDialogue as PathwayDialogue;
            dialog.edit(pathway.id);
        }

        pathwayAdd(): void {
            if(Utils.isEmptyId(this.briefing.id)) {
                alert("Briefing ID is not set!");
                return;
            }
            const pathwayDialogue: PathwayDialogue = this.$refs.pathwayDialogue as PathwayDialogue;
            pathwayDialogue.add(this.briefing.id);
        }

        sectionAdd(item: BriefingItemContainer|null|undefined, isQuiz: boolean) {
            const items = isQuiz ? this.quizPageList : this.briefingPageList;

            let displayOrder = -1;
            let insertAtIndex = -1;

            if (item?.isPage) {
                // ok
                let lastSection: BriefingSection | null = null;
                for (let i = 0; i < items.length; i++) {
                    const itm = items[i];
                    if (itm.section) lastSection = itm.section;
                    if (itm.page?.id === item.id) {
                        insertAtIndex = i;
                        displayOrder = lastSection?.displayOrder ?? 0;
                        break;
                    }
                }
            }
            else if (item?.isSection) {
                // ok
                displayOrder = (item.section?.displayOrder ?? 1) - 1;
                insertAtIndex = items.findIndex(i => i.id === item.section?.id);
                if (insertAtIndex < 0) insertAtIndex = 0;
            }

            if (displayOrder === -1) displayOrder = 0;
            if (insertAtIndex < 0) insertAtIndex = 0;

            const section = new BriefingSection({
                id: Utils.createKey(6),
                displayOrder,
                description: "New Section",
                isQuiz,
                isExpanded: true,
                count: 0 // todo
            });

            items.splice(insertAtIndex, 0, new BriefingItemContainer({
                isMenuShowing: false,
                section
            }));

            this.renumberDisplayOrder(items);
            this.savePages();
            this.saveSections();
        }

        // called from BriefingMatrixSection.vue component
        async sectionSave(item: BriefingItemContainer) {
            if (!item?.section) return;
            if (Utils.isEmptyOrWhitespace(item.section.description)) {
                item.section.description = "Section";  // we don't want empty section names
            }
            this.saveSections();
        }

        // called from BriefingMatrixSection.vue component
        async sectionRemove(item: BriefingItemContainer) {
            if(!item.section) return;
            // find index
            const items = item.isQuiz ? this.quizPageList : this.briefingPageList;
            const whereRemoving = this.findStartAndEndOfSection(item.section);

            if(whereRemoving.startAt === -1) return;          
            // remove from list
            items.splice(whereRemoving.startAt, 1);

            const prevSection = this.findSection(items[whereRemoving.startAt - 1]?.page, false);

            // set pages from removed section to be same as above section
            for(let i = whereRemoving.startAt; i <= whereRemoving.endAt; i++) {
                const itm = items[i];
                if(!itm?.page) continue;
//console.log(`......sect ${itm.page.sectionID} => ${prevSection?.id ?? ""}  (${prevSection?.description ?? ""})`);
                itm.page.sectionID = prevSection?.id ?? "";
                itm.page.sectionDisplayOrder = prevSection?.displayOrder ?? 0;
            }

            // save new page sections and delete on server
            await this.savePages();
            await this.saveSections();
        }

        pageAdd(item: BriefingItemContainer|null|undefined, isQuiz: boolean): void {
            if(Utils.isEmptyId(this.briefing.id)) {
                toastr.error("Briefing ID is not set!");
                return;
            }
            let displayOrder = -1;
            let sectionID = "";

            if (item?.isPage) {
                // one after the page selected
                displayOrder = (item.page?.displayOrder ?? 0) + 1;
                sectionID = item.page?.sectionID ?? "";
            }
            else if (item?.isSection) {
                // end of selected section
                sectionID = item.section?.id ?? "";                
                let foundThisSection = false;
                for (const i of item.isQuiz ? this.quizPageList : this.briefingPageList) {
                    if(i.id === item.id) {
                        foundThisSection = true;
                        continue;
                    }
                    if (foundThisSection && i.isPage) displayOrder = (i.page?.displayOrder ?? 0) + 1;
                    if (foundThisSection && i.isSection) break; // keep going until the next section or the end of the list
                }
            }

            if(displayOrder === -1) {
                // put at the end
                const briefingPageList = (this.briefingPageList as Array<any>).filter(i => i.isSection).map(i => i.section);
                const quizPageList = (this.quizPageList as Array<any>).filter(i => i.isPage).map(i => i.page);
                displayOrder = isQuiz
                    ? this.maxPageDisplayOrder(quizPageList, true) + 1
                    : this.maxPageDisplayOrder(briefingPageList, false) + 1;
            }

            this.$emit("page-add", isQuiz, sectionID, displayOrder);
        }

        pageEdit(page: Page|undefined, event: MouseEvent): void {
            if(!page) return;
            this.$emit("page-edit", page.id);
            this.checkEmptyRowsAndColumns();
        }

        //
        // -- misc helper functions
        //

        readonly quizDisplayOrderOffset = 100000;

        // assuming pages are in correct order, make sure display-order and section IDs are consistent
        renumberDisplayOrder(items: Array<BriefingItemContainer>) {
            if (!items?.length) return;
            let pageOrder = items[0].isQuiz ? this.quizDisplayOrderOffset : 0;
            let sectionOrder = items[0].isQuiz ? this.quizDisplayOrderOffset : 0;
            let lastSection: BriefingSection|null = null;
            for (const item of items) {
                if (item.section)  {
                    item.section.displayOrder = ++sectionOrder;
                    lastSection = item.section;
                }
                else if (item.page) {
                    item.page.displayOrder = ++pageOrder;
                    item.page.sectionID = lastSection?.id ?? ""; 
                    item.page.sectionDisplayOrder = lastSection?.displayOrder ?? 0;
                }
            }
        }

        maxPageDisplayOrder(pages: Array<Page>, isQuiz: boolean): number {
            if(pages.length === 0) return isQuiz ? this.quizDisplayOrderOffset : 0;
            return Math.max.apply(Math, pages.map(q => q.displayOrder));
        }

        addNewTopSection(isQuiz: boolean): BriefingSection {
            const items = isQuiz ? this.quizPageList : this.briefingPageList;
            const section = new BriefingSection({
                id: Utils.createKey(6),
                displayOrder: 0,
                description: "New Section",
                isQuiz,
                isExpanded: true,
                count: 0 // todo
            });
            items.unshift(new BriefingItemContainer({ isMenuShowing: false, section }));
            return section;
        }

        // find first section above given page
        findSection(page: Page | undefined, shouldCreateIfNone: boolean): BriefingSection | null {
            if(!page) return null;
            const items = page.isQuiz ? this.quizPageList : this.briefingPageList;
            let pageIndex = -1;
            let sectionIndex = -1;
            for (let i = items.length - 1; i >= 0; i--) {
                const item = items[i];
                if (pageIndex > -1 && item.isSection) {
                    sectionIndex = i;
                    break;
                }
                if (item.page?.id === page.id) pageIndex = i;
            }
            if (sectionIndex == -1 && !shouldCreateIfNone) return null;
            return sectionIndex == -1
                ? this.addNewTopSection(page.isQuiz)
                : items[sectionIndex].section!;
        }

        findStartAndEndOfSection(section: BriefingSection|null|undefined, isQuiz?: boolean) : { startAt: number; endAt: number; } {
            isQuiz = isQuiz ?? section?.isQuiz;
            if(typeof isQuiz !== "boolean") return { startAt: -1, endAt: -1 }; // need to know what list...
            const items = isQuiz ? this.quizPageList : this.briefingPageList;
            if(section == null && items[0]?.isSection) return  { startAt: -1, endAt: -1 }; // if section is null, might mean pages at start - but not if first item is section...
            let sectionIndex = section == null ? 0 : -1; // if no section, assume start
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                // now found the NEXT section?
                if (sectionIndex > -1 && item.isSection) return {
                    startAt: sectionIndex,
                    endAt: i - 1 // go back one to get last
                };
                if (section != null && item.section?.id === section.id) sectionIndex = i; // we've now found the section we're intrerested in...
            }
            // we reached the end without finding another section
            return {
                startAt: sectionIndex,
                endAt: items.length - 1
            };
        }


        async savePages() {
            const allItems = this.briefingPageList.concat(this.quizPageList).filter(i => i.isPage).map(i => i.page as Page);
            await ApiClient.post("/api/page/saveAll", allItems);
        }

        async saveSections() {
            const briefingSections = this.briefingPageList.filter(i => i.section != null).map(i => i.section) as Array<BriefingSection>;
            const quizSections = this.quizPageList.filter(i => i.section != null).map(i => i.section) as Array<BriefingSection>;
            this.dontSaveBriefingOnChange = true;
            Vue.nextTick(async () => {
                this.briefing.sections = briefingSections.concat(quizSections);
                await ApiClient.post("/api/briefing/save", this.briefing);            
                this.dontSaveBriefingOnChange = false;
            });
        }
    }
